import styled from "styled-components";
import { ShowState } from "./types";

// New shows read green, regular active shows white, and stopping shows red.
// Notes (no state) fall back to white.
const stateColor = (
  state: ShowState | undefined,
  theme: { colors: { green: string; white: string; red: string } }
): string => {
  if (state === "new") return theme.colors.green;
  if (state === "stopping") return theme.colors.red;
  return theme.colors.white;
};

// Faint gridlines that read like react-big-calendar's default light borders on
// the dark theme, plus a slightly stronger line to delimit sections.
const GRID_LINE = "rgba(255, 255, 255, 0.08)";
const GRID_LINE_STRONG = "rgba(255, 255, 255, 0.16)";
// Subtle brand-tinted rule marking the start of each 4-week block (the line
// between one block's W4 and the next block's W1).
const MONTH_LINE = "rgba(255, 240, 87, 0.28)";
const MONTH_LINE_WIDTH = "2.5px";

export const BoardTitle = styled.h1`
  margin: 0;
  font-weight: 400;
  white-space: nowrap;
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: clamp(14px, 1.6vw, 20px);
  line-height: 1.1;
`;

/** Slim header row: the view toggle sits top-left, the title after it. */
export const BoardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.medium};
  width: 100%;
`;

/** Segmented Board / Master Schedule switch. */
export const ViewToggle = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  padding: 2px;
  background-color: ${({ theme }): string => theme.colors.darkBg};
  border: 1px solid ${GRID_LINE_STRONG};
  border-radius: 20px;
`;

export const ViewToggleButton = styled.button<{ $active?: boolean }>`
  padding: 4px 12px;
  background-color: ${({ $active, theme }): string =>
    $active ? theme.colors.yellow : "transparent"};
  border: none;
  border-radius: 18px;
  color: ${({ $active, theme }): string =>
    $active ? theme.colors.darkBg : theme.colors.white};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: ${({ $active, theme }): string =>
      $active ? theme.colors.darkBg : theme.colors.yellow};
  }
`;

/**
 * Pill toggle pinned to the top-right of the header. Flips the grid blocks to a
 * solid white background with black text.
 */
export const WhiteBlocksToggle = styled.button<{ $active?: boolean }>`
  margin-left: auto;
  flex-shrink: 0;
  padding: 4px 12px;
  background-color: ${({ $active, theme }): string =>
    $active ? theme.colors.white : "transparent"};
  border: 1px solid
    ${({ $active, theme }): string =>
      $active ? theme.colors.white : GRID_LINE_STRONG};
  border-radius: 20px;
  color: ${({ $active, theme }): string =>
    $active ? theme.colors.black : theme.colors.white};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${({ theme }): string => theme.colors.white};
  }
`;

export const BoardLayout = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }): string => theme.margins.medium};
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: ${({ theme }): string => theme.margins.medium};
  box-sizing: border-box;
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  width: 240px;
  min-width: 240px;
  background-color: ${({ theme }): string => theme.colors.darkerBg};
  border: 1px solid ${GRID_LINE_STRONG};
  border-radius: 8px;
  padding: ${({ theme }): string => theme.margins.small};
  box-sizing: border-box;
  overflow: hidden;
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: ${({ theme }): string => theme.margins.xsmall}
    ${({ theme }): string => theme.margins.xsmall}
    ${({ theme }): string => theme.margins.small};
`;

export const SidebarTitle = styled.h3`
  margin: 0;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 13px;
  font-weight: 500;
`;

export const SidebarCount = styled.span`
  color: ${({ theme }): string => theme.colors.gray};
  font-size: 12px;
`;

/**
 * Multi-select filter chips sitting between the header and the search box.
 * Wraps to a second row so all filters stay legible in the narrow sidebar.
 */
export const FilterControl = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: ${({ theme }): string => theme.margins.small};
`;

export const FilterButton = styled.button<{ $active?: boolean }>`
  flex: 1 1 auto;
  padding: 5px 10px;
  background-color: ${({ $active, theme }): string =>
    $active ? theme.colors.yellow : theme.colors.darkBg};
  border: 1px solid
    ${({ $active, theme }): string =>
      $active ? theme.colors.yellow : theme.colors.gray};
  border-radius: 20px;
  color: ${({ $active, theme }): string =>
    $active ? theme.colors.darkBg : theme.colors.white};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }): string => theme.colors.yellow};
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin-bottom: ${({ theme }): string => theme.margins.small};
  padding: 7px 12px;
  background-color: ${({ theme }): string => theme.colors.darkBg};
  border: 1px solid ${({ theme }): string => theme.colors.gray};
  border-radius: 20px;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 13px;

  &::placeholder {
    color: ${({ theme }): string => theme.colors.gray200};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }): string => theme.colors.yellow};
  }
`;

export const SidebarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }): string => theme.margins.xsmall};
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-right: 2px;
`;

export const SidebarEmpty = styled.div`
  color: ${({ theme }): string => theme.colors.gray};
  font-size: 12px;
  text-align: center;
  padding: ${({ theme }): string => theme.margins.medium};
`;

/**
 * Compact event block sized to sit inside a thin grid row (like a
 * react-big-calendar event). Text is green when the show is active and red
 * when it is inactive.
 */
export const ShowBoxRoot = styled.div<{
  $dragging?: boolean;
  $state?: ShowState;
  $readOnly?: boolean;
  // "White blocks" mode: a solid white block with black text, overriding the
  // per-state text colour (badges and curator names go black too, below).
  $whiteBlocks?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.xsmall};
  padding: 3px 8px;
  background-color: ${({ $whiteBlocks, theme }): string =>
    $whiteBlocks ? theme.colors.white : theme.colors.darkGray};
  border: 1px solid ${GRID_LINE_STRONG};
  border-radius: 6px;
  color: ${({ $whiteBlocks, $state, theme }): string =>
    $whiteBlocks ? theme.colors.black : stateColor($state, theme)};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  cursor: ${({ $readOnly }): string => ($readOnly ? "default" : "grab")};
  user-select: none;
  touch-action: none;
  opacity: ${({ $dragging }): number => ($dragging ? 0.4 : 1)};
  transition: border-color 0.15s ease;

  ${({ $whiteBlocks, theme }): string =>
    $whiteBlocks ? `& span { color: ${theme.colors.black}; }` : ""}

  &:hover {
    border-color: ${({ $readOnly, theme }): string =>
      $readOnly ? GRID_LINE_STRONG : theme.colors.yellow};
  }
`;

/** Stacks multiple shows that fall on the same day/week in the master view. */
export const MasterCellStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  height: 100%;
`;

/**
 * Compact next-air date ("MM.DD") shown on a stopping show, pulled from the
 * master schedule. Rendered in the red state colour to read as part of the
 * stopping treatment.
 */
export const NextAirDate = styled.span`
  flex-shrink: 0;
  margin-left: 4px;
  color: ${({ theme }): string => theme.colors.red};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

/** Muted archived-playlist count rendered right after the show title. */
export const CountBadge = styled.span`
  margin-left: 4px;
  color: ${({ theme }): string => theme.colors.gray200};
  font-weight: 500;
`;

/**
 * Submitted-playlist count shown in green in place of the archived count on
 * shows with no archives, so a not-yet-archived show still surfaces how many
 * playlists have been submitted.
 */
export const SubmittedCountBadge = styled.span`
  margin-left: 4px;
  color: ${({ theme }): string => theme.colors.green};
  font-weight: 500;
`;

export const ShowBoxTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

/** Main curator name shown after the show title, in a muted, lighter weight. */
export const ShowBoxCurator = styled.span`
  flex-shrink: 0;
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }): string => theme.colors.gray200};
  font-weight: 500;
`;

export const GridPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }): string => theme.colors.darkerBg};
  border: 1px solid ${GRID_LINE_STRONG};
  border-radius: 8px;
  overflow: auto;
`;

// Both grids share a leading thin hour-axis column (labels each 4-week block
// with its time-of-day, written sideways) followed by the week-label column.
const HOUR_COL_WIDTH = "20px";
const WEEK_COL_WIDTH = "50px";

/**
 * A leading hour-axis column, a week-label column, and 7 day columns. The
 * header row stays pinned while the grid body scrolls.
 */
export const GridTable = styled.div`
  display: grid;
  grid-template-columns: ${HOUR_COL_WIDTH} ${WEEK_COL_WIDTH} repeat(
      7,
      minmax(110px, 1fr)
    );
  min-width: fit-content;
`;

// Header corner pinned above the hour-axis column (far left).
export const GridCornerCell = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 3;
  background-color: ${({ theme }): string => theme.colors.darkerBg};
  border-right: 1px solid ${GRID_LINE};
  border-bottom: 1px solid ${GRID_LINE_STRONG};
`;

// Header corner above the week-label column, offset past the hour axis.
export const WeekCornerCell = styled(GridCornerCell)`
  left: ${HOUR_COL_WIDTH};
`;

export const GridHeaderCell = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ theme }): string => theme.colors.darkerBg};
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  padding: 7px 4px;
  border-right: 1px solid ${GRID_LINE};
  border-bottom: 1px solid ${GRID_LINE_STRONG};
`;

// Week-label column, sitting just right of the hour axis.
export const WeekLabelCell = styled.div<{ $monthStart?: boolean }>`
  position: sticky;
  left: ${HOUR_COL_WIDTH};
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }): string => theme.colors.darkerBg};
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 10px;
  font-weight: 600;
  padding: 2px;
  border-right: 1px solid ${GRID_LINE};
  border-bottom: 1px solid ${GRID_LINE};
  border-top: ${({ $monthStart }): string =>
    $monthStart ? `${MONTH_LINE_WIDTH} solid ${MONTH_LINE}` : "none"};
`;

// One label per 4-week block, pinned to the far-left hour-axis column. Spans
// the block's rows and renders the hour sideways, kept as thin as possible.
export const HourLabelCell = styled.div<{ $span: number }>`
  grid-row: span ${({ $span }): number => $span};
  position: sticky;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  white-space: nowrap;
  background-color: ${({ theme }): string => theme.colors.darkerBg};
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 4px 0;
  border-right: 1px solid ${GRID_LINE};
  border-bottom: 1px solid ${GRID_LINE};
  border-top: ${MONTH_LINE_WIDTH} solid ${MONTH_LINE};
`;

export const GridCell = styled.div<{
  $isOver?: boolean;
  $occupied?: boolean;
  $invalidOver?: boolean;
  $monthStart?: boolean;
}>`
  position: relative;
  min-height: 26px;
  padding: 2px;
  box-sizing: border-box;
  border-right: 1px solid ${GRID_LINE};
  border-bottom: 1px solid ${GRID_LINE};
  border-top: ${({ $monthStart }): string =>
    $monthStart ? `${MONTH_LINE_WIDTH} solid ${MONTH_LINE}` : "none"};
  background-color: ${({ $isOver, $invalidOver, theme }): string => {
    if ($invalidOver) return "rgba(255, 65, 22, 0.15)";
    if ($isOver) return theme.colors.opacity_yellow;
    return "transparent";
  }};
  transition: background-color 0.12s ease;
`;

/**
 * Comparison-mode cell: the board (editable) half on the left, the read-only
 * master-schedule half on the right, divided by a gridline. Collapses to a
 * single unified block when both sides hold the same show.
 */
export const CompSplit = styled.div`
  display: flex;
  height: 100%;
  min-height: 22px;
`;

/**
 * Apply action shown on hover in the top-right of the orange master half when
 * the board and master sides of a comparison slot hold different shows. Clicking
 * it schedules the board show into that master slot.
 */
export const ApplyButton = styled.button`
  position: absolute;
  top: 1px;
  right: 1px;
  display: none;
  align-items: center;
  justify-content: center;
  height: 14px;
  padding: 0 5px;
  background-color: ${({ theme }): string => theme.colors.orange};
  border: none;
  border-radius: 7px;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
  cursor: pointer;
  z-index: 3;

  &:hover {
    filter: brightness(1.1);
  }

  &:disabled {
    background-color: ${({ theme }): string => theme.colors.gray200};
    cursor: not-allowed;
    filter: none;
    opacity: 0.8;
  }
`;

export const CompHalf = styled.div<{
  $isOver?: boolean;
  $invalidOver?: boolean;
  $orange?: boolean;
}>`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  padding: 1px;
  box-sizing: border-box;
  background-color: ${({ $isOver, $invalidOver, $orange, theme }): string => {
    if ($invalidOver) return "rgba(255, 65, 22, 0.15)";
    if ($isOver) return theme.colors.opacity_yellow;
    if ($orange) return "rgba(222, 105, 32, 0.22)";
    return "transparent";
  }};
  transition: background-color 0.12s ease;

  & + & {
    border-left: 1px solid ${GRID_LINE_STRONG};
  }

  &:hover ${ApplyButton} {
    display: flex;
  }
`;

/** Faint "board" / "master" tag shown in an empty comparison half. */
export const CompHalfTag = styled.span`
  position: absolute;
  top: 1px;
  left: 3px;
  color: ${({ theme }): string => theme.colors.gray200};
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.5;
  pointer-events: none;
`;

export const RemoveButton = styled.button`
  position: absolute;
  top: 1px;
  right: 1px;
  display: none;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  background-color: ${({ theme }): string => theme.colors.red};
  border: none;
  border-radius: 50%;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
  z-index: 2;
`;

/**
 * Black asterisk in a yellow circle, mirrored in the top-left corner opposite
 * the remove button. Opens the local state menu for a placed show.
 */
export const StateButton = styled.button`
  position: absolute;
  top: 1px;
  left: 1px;
  display: none;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  background-color: ${({ theme }): string => theme.colors.yellow};
  border: none;
  border-radius: 50%;
  color: ${({ theme }): string => theme.colors.black};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  z-index: 2;
`;

export const PlacedShowWrapper = styled.div`
  height: 100%;

  &:hover ${RemoveButton}, &:hover ${StateButton} {
    display: flex;
  }
`;

/** Full-viewport catcher so clicking anywhere closes the state menu. */
export const StateMenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
`;

export const StateMenu = styled.div<{ $x: number; $y: number }>`
  position: fixed;
  top: ${({ $y }): string => `${$y}px`};
  left: ${({ $x }): string => `${$x}px`};
  z-index: 41;
  display: flex;
  flex-direction: column;
  min-width: 120px;
  padding: 4px;
  background-color: ${({ theme }): string => theme.colors.darkGray};
  border: 1px solid ${GRID_LINE_STRONG};
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
`;

export const StateMenuItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background-color: ${({ $active, theme }): string =>
    $active ? theme.colors.darkBg : "transparent"};
  border: none;
  border-radius: 6px;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 12px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }): string => theme.colors.darkBg};
  }
`;

/** Small colour dot preceding each state label in the menu. */
export const StateDot = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }): string => $color};
`;

export const DragOverlayBox = styled.div<{
  $state?: ShowState;
  $whiteBlocks?: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 3px 8px;
  background-color: ${({ $whiteBlocks, theme }): string =>
    $whiteBlocks ? theme.colors.white : theme.colors.darkGray};
  border: 1px solid ${({ theme }): string => theme.colors.yellow};
  border-radius: 6px;
  color: ${({ $whiteBlocks, $state, theme }): string =>
    $whiteBlocks ? theme.colors.black : stateColor($state, theme)};
  font-size: 11px;
  font-weight: 600;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
`;

export const StatusText = styled.div`
  color: ${({ theme }): string => theme.colors.gray};
  font-size: 13px;
  padding: ${({ theme }): string => theme.margins.medium};
`;

/** Upper "Shows" area — takes the free vertical space and scrolls its list. */
export const ShowsSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

/** Lower "Note Shows" area, capped so it never crowds out the shows list. */
export const NotesSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  min-height: 0;
  max-height: 45%;
  margin-top: ${({ theme }): string => theme.margins.small};
  padding-top: ${({ theme }): string => theme.margins.small};
  border-top: 1px solid ${GRID_LINE_STRONG};
`;

export const NoteInputRow = styled.form`
  display: flex;
  gap: 4px;
  margin-bottom: ${({ theme }): string => theme.margins.small};
`;

export const NoteInput = styled.input`
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  padding: 7px 12px;
  background-color: ${({ theme }): string => theme.colors.darkBg};
  border: 1px solid ${({ theme }): string => theme.colors.gray};
  border-radius: 20px;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 13px;

  &::placeholder {
    color: ${({ theme }): string => theme.colors.gray200};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }): string => theme.colors.yellow};
  }
`;

export const NoteAddButton = styled.button`
  flex-shrink: 0;
  padding: 0 12px;
  background-color: ${({ theme }): string => theme.colors.yellow};
  border: 1px solid ${({ theme }): string => theme.colors.yellow};
  border-radius: 20px;
  color: ${({ theme }): string => theme.colors.darkBg};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const NoteRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  & > *:first-child {
    flex: 1;
    min-width: 0;
  }
`;

export const NoteDeleteButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  background-color: transparent;
  border: none;
  border-radius: 50%;
  color: ${({ theme }): string => theme.colors.gray};
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: ${({ theme }): string => theme.colors.red};
  }
`;

/** Body copy for the comparison-view "Apply" confirmation dialog. */
export const ConfirmText = styled.p`
  margin: 0;
  max-width: 340px;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
`;

/** Emphasised show title / date-time inside the confirmation copy. */
export const ConfirmHighlight = styled.span`
  color: ${({ theme }): string => theme.colors.orange};
  font-weight: 700;
`;

/** Error message shown in the confirmation dialog when Apply fails. */
export const ConfirmError = styled.p`
  margin: 12px 0 0;
  color: ${({ theme }): string => theme.colors.red};
  font-size: 12px;
  text-align: center;
`;

export const ConfirmCancelButton = styled.button`
  padding: 8px 18px;
  background-color: transparent;
  border: 1px solid ${({ theme }): string => theme.colors.gray200};
  border-radius: 20px;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const ConfirmApplyButton = styled.button`
  padding: 8px 18px;
  background-color: ${({ theme }): string => theme.colors.orange};
  border: 1px solid ${({ theme }): string => theme.colors.orange};
  border-radius: 20px;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/** Right-aligned group holding Save / Pull actions and the White-blocks toggle. */
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.xsmall};
  margin-left: auto;
`;

/** Pill button for the header Save / Pull actions. `$primary` fills it yellow. */
export const HeaderActionButton = styled.button<{ $primary?: boolean }>`
  flex-shrink: 0;
  padding: 4px 12px;
  background-color: ${({ $primary, theme }): string =>
    $primary ? theme.colors.yellow : "transparent"};
  border: 1px solid
    ${({ $primary, theme }): string =>
      $primary ? theme.colors.yellow : GRID_LINE_STRONG};
  border-radius: 20px;
  color: ${({ $primary, theme }): string =>
    $primary ? theme.colors.darkBg : theme.colors.white};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }): string => theme.colors.yellow};
    color: ${({ $primary, theme }): string =>
      $primary ? theme.colors.darkBg : theme.colors.yellow};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/** Small transient status text ("Saved" / error) next to the Save button. */
export const SaveStatus = styled.span<{ $error?: boolean }>`
  font-size: 11px;
  white-space: nowrap;
  color: ${({ $error, theme }): string =>
    $error ? theme.colors.red : theme.colors.gray};
`;

/** Secondary confirm-dialog action (Merge), sitting between Cancel and Replace. */
export const PullMergeButton = styled.button`
  padding: 8px 18px;
  background-color: transparent;
  border: 1px solid ${({ theme }): string => theme.colors.yellow};
  border-radius: 20px;
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
